// Pure URL builders + response parsing + paging logic for the PDOK Locatieserver.
// Kept free of `fetch` so every branch is unit-testable; the async wrapper lives
// in fetchPdok.js.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (External data section)

import { PDOK_FL } from '../pdokLocation'

const PDOK_BASE = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1'

// Locatieserver hard limits: max 50 rows per page, and `start` cannot exceed
// 10.000 (deep-paging cap). A wijk with more addresses than that must be paged
// per-buurt or per-postcode instead.
const PDOK_MAX_ROWS = 50
const PDOK_MAX_START = 10000

const GEMEENTE_FL = 'gemeentenaam,gemeentecode,weergavenaam,id'
const WIJK_FL = 'wijknaam,wijkcode,gemeentenaam,gemeentecode,weergavenaam,id'
const BUURT_FL = 'buurtnaam,buurtcode,wijkcode,gemeentecode,weergavenaam,id'

const freeUrl = (params) => `${PDOK_BASE}/free?${params.toString()}`

const buildGemeenteSearchUrl = (query) => {
    const params = new URLSearchParams({
        q: query || '',
        fq: 'type:gemeente',
        rows: String(PDOK_MAX_ROWS),
        fl: GEMEENTE_FL,
    })
    return freeUrl(params)
}

const buildWijkListUrl = (gemeentenaam, { rows = PDOK_MAX_ROWS, start = 0 } = {}) => {
    const params = new URLSearchParams({ q: '*', rows: String(rows), start: String(start), fl: WIJK_FL })
    params.append('fq', 'type:wijk')
    params.append('fq', `gemeentenaam:"${gemeentenaam}"`)
    return freeUrl(params)
}

const buildBuurtListUrl = (wijkcode, { rows = PDOK_MAX_ROWS, start = 0 } = {}) => {
    const params = new URLSearchParams({ q: '*', rows: String(rows), start: String(start), fl: BUURT_FL })
    params.append('fq', 'type:buurt')
    params.append('fq', `wijkcode:${wijkcode}`)
    return freeUrl(params)
}

// Filter precedence: buurtcode (finest) → wijkcode → postcode. Exactly one is used.
const buildAdresListUrl = ({ wijkcode, buurtcode, postcode, rows = PDOK_MAX_ROWS, start = 0 } = {}) => {
    const params = new URLSearchParams({ q: '*', rows: String(rows), start: String(start), fl: PDOK_FL })
    params.append('fq', 'type:adres')
    if (buurtcode) params.append('fq', `buurtcode:${buurtcode}`)
    else if (wijkcode) params.append('fq', `wijkcode:${wijkcode}`)
    else if (postcode) params.append('fq', `postcode:${postcode}`)
    return freeUrl(params)
}

/** '7201 ab' → '7201AB'; '12a' → '12'. PDOK houdt letter en toevoeging apart. */
const postcodeEnNummer = ({ postcode, huisnummer }) => ({
    pc: String(postcode || '').toUpperCase().replace(/\s+/g, ''),
    nummer: (String(huisnummer || '').trim().match(/^\d+/) || [''])[0],
})

/**
 * Alle woningen op één postcode + huisnummer (exact), zodat verifyAddressMatch
 * de woning met de juiste toevoeging kan kiezen. Spiegel van
 * buildHouseholdPdokUrl in functions/adressHelpers/householdLocation.js — de
 * pariteit staat in functions/addressMatching/lookupBagServerSide.test.js.
 * @param {{postcode: string, huisnummer: string}} input - Het huisnummer mag een toevoeging bevatten; die gaat er hier af
 * @returns {string}
 */
const buildHuisnummerUnitsUrl = (input) => {
    const { pc, nummer } = postcodeEnNummer(input)
    const params = new URLSearchParams({
        q: `postcode:${pc} and huisnummer:${nummer}`,
        rows: String(PDOK_MAX_ROWS),
        fl: PDOK_FL,
    })
    return freeUrl(params)
}

/**
 * Vrij zoeken op postcode + nummer, alleen voor de hint 'horen niet bij
 * elkaar … vond: <adres>'. De exacte vraag hierboven geeft bij een onbekend
 * nummer niets terug; deze vindt de buren.
 * @param {{postcode: string, huisnummer: string}} input
 * @returns {string}
 */
const buildAddressHintUrl = (input) => {
    const { pc, nummer } = postcodeEnNummer(input)
    const params = new URLSearchParams({ q: `${pc} ${nummer}`, fq: 'type:adres', rows: '5', fl: PDOK_FL })
    return freeUrl(params)
}

const parsePdokResponse = (json) => {
    const response = json && json.response
    return {
        numFound: (response && response.numFound) || 0,
        docs: Array.isArray(response && response.docs) ? response.docs : [],
    }
}

const exceedsDeepPagingCap = (numFound) => numFound > PDOK_MAX_START

// All `start` offsets needed to walk every page, capped at the deep-paging limit.
const buildStartOffsets = (numFound, rows = PDOK_MAX_ROWS) => {
    if (!numFound || numFound <= 0) return []
    const offsets = []
    for (let start = 0; start < numFound && start <= PDOK_MAX_START; start += rows) {
        offsets.push(start)
    }
    return offsets
}

export {
    PDOK_BASE,
    PDOK_MAX_ROWS,
    PDOK_MAX_START,
    buildGemeenteSearchUrl,
    buildWijkListUrl,
    buildBuurtListUrl,
    buildAdresListUrl,
    buildHuisnummerUnitsUrl,
    buildAddressHintUrl,
    parsePdokResponse,
    exceedsDeepPagingCap,
    buildStartOffsets,
}
